import {
  createUnifiedTheme,
  palettes,
  type UnifiedTheme,
} from '@backstage/theme';

/*
 * Backstage's stock light theme ships a DARK sidebar (`palette.navigation` is
 * near-black), which is why the pre-redesign sidebar used light-grey text. The
 * redesign inverts that: the sidebar is the panel tier, one step off the app
 * canvas, with dark text and hone-yellow as the active indicator.
 *
 * `palette.navigation` is the only route to those colours — `BackstageSidebarItem`
 * reads them directly from the MUI theme, so they cannot be set through the
 * `--bui-*` bridge. Geometry (the raised active pill) lives in `hone-portal.css`;
 * this file is colour only.
 *
 * Values are the resolved Helix hexes. MUI v4's theme is a plain JS object
 * consumed outside any CSS cascade, so `var(--helix-*)` would not resolve here —
 * this is the one place the redesign spells hexes out, and each is annotated
 * with the token it mirrors.
 */
const ink = '#202020'; /* neutral-900 */
const charcoal = '#363636'; /* neutral-800 */
const secondary = '#6d6d6d'; /* neutral-500 */
const panel = '#f8f8f8'; /* neutral-50 */
const inset = '#f4f4f4'; /* neutral-75 */
const honeYellow = '#f8f93f'; /* brand-500 */

export const honeTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    // The mock frames content on white, but Backstage's light palette defaults to a
    // grey `background.default` that `Content`/`Page` paint around the content, on top
    // of the white body (`--hone-portal-bg-app`). Override it so the canvas is white
    // end to end. Literal hex — `var(--*)` does not resolve in the MUI JS theme.
    background: {
      ...palettes.light.background,
      default: '#ffffff', // app canvas white, matching the mock's screen frames
    },
    // Backstage's default primary is a muted blue (#1f5493); point MUI links and
    // primary accents at the Hone accent blue. Sidebar submenu items read
    // `navigation.color`, not primary, so they stay dark rather than turning blue.
    primary: {
      main: '#039cfb', // accent-blue-500
      dark: '#027ac4', // accent-blue-600
    },
    // Backstage carries its own `link`/`linkHover` palette keys alongside
    // `primary`, and they keep their stock Backstage blue (#0A6EBE) through a
    // `primary` override. They are what the MUI tab row, the TechDocs reader's
    // CSS variables and MarkdownContent read, so links rendered through those
    // paths stayed off-theme. Helix ships no dedicated link token; accent blue
    // is the link colour, matching the `a` rules in hone-portal.css.
    link: '#039cfb', // accent-blue-500
    linkHover: '#027ac4', // accent-blue-600
    navigation: {
      background: panel,
      indicator: honeYellow,
      color: '#4a4a4a', // resting label: a step lighter than charcoal
      selectedColor: ink,
      navItem: { hoverBackground: inset },
      submenu: {
        background: panel,
      },
    },
  },
  fontFamily: "'DM Sans', system-ui, sans-serif",
  defaultPageTheme: 'home',
});

export const honeThemeColors = {
  ink,
  charcoal,
  secondary,
  panel,
  inset,
  honeYellow,
};

/*
 * Dark twin of `honeTheme` above. Same construction — `createUnifiedTheme`
 * over `palettes.dark`, literal hexes annotated with the token/role they
 * mirror, since MUI's JS theme can't resolve `var(--helix-*)`. Accent blue
 * (`primary`/`link`/`linkHover`) is unchanged from light — it's a hue token,
 * not a surface, and already reads correctly on a dark canvas.
 */
const darkCanvas = '#262626'; /* dark canvas — literal, no Helix dark token yet */
const darkPanel = '#1c1c1c'; /* dark panel/sidebar — literal */
const darkHover = '#333333'; /* nav item hover — literal */
const darkSecondary = '#a0a0a0'; /* off-ramp grey label — literal */
const darkSelected = '#ececec'; /* selected label — literal */

export const honeDarkTheme: UnifiedTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    background: {
      ...palettes.dark.background,
      default: darkCanvas,
      // MUI Paper surfaces (entity tabs, menus, dialogs, autocomplete listbox,
      // InfoCards) otherwise fall through to Backstage's stock dark grey; pin
      // them to the dark canvas so they sit flush with the app background.
      paper: darkCanvas,
    },
    primary: {
      main: '#039cfb', // accent-blue-500
      dark: '#027ac4', // accent-blue-600
    },
    link: '#039cfb', // accent-blue-500
    linkHover: '#027ac4', // accent-blue-600
    navigation: {
      background: darkPanel,
      indicator: honeYellow,
      color: darkSecondary,
      selectedColor: darkSelected,
      navItem: { hoverBackground: darkHover },
      submenu: {
        background: darkPanel,
      },
    },
  },
  fontFamily: "'DM Sans', system-ui, sans-serif",
  defaultPageTheme: 'home',
});
